import { cn } from '../../lib/utils';
import { Card, CardContent } from './card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';

const PAGE_SHELL = 'mx-auto w-full max-w-6xl px-4 py-6 md:px-6 lg:px-8';
const PAGE_SHELL_WIDE = 'w-full px-4 py-6 md:px-6 lg:px-8 2xl:px-10';

function Skeleton({ className, ...props }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-black/[0.08] dark:bg-white/[0.08]',
        className
      )}
      {...props}
    />
  );
}

function PageHeaderSkeleton() {
  return (
    <div className="mb-8 space-y-3">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-9 w-full max-w-md" />
      <Skeleton className="h-4 w-full max-w-2xl" />
    </div>
  );
}

function TableSkeleton({
  columns = 5,
  rows = 8,
  columnWidths,
  className,
  framed = true,
  footer = null,
}) {
  const widths = columnWidths || Array.from({ length: columns }, (_, i) =>
    (i === 0 ? 'w-8' : 'w-full min-w-[100px]')
  );

  return (
    <Card className={cn('w-full overflow-hidden', className)}>
      <CardContent className="p-0">
        <Table framed={framed}>
          <TableHeader>
            <TableRow>
              {Array.from({ length: columns }).map((_, i) => (
                <TableHead key={i} className={i === 0 ? 'w-10 min-w-[2.5rem]' : 'min-w-[140px]'}>
                  <Skeleton className="h-3 w-16" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <TableCell key={colIndex} className={colIndex === 0 ? 'w-10 min-w-[2.5rem]' : 'min-w-[140px]'}>
                    <Skeleton className={cn('h-3.5', widths[colIndex] || 'w-full min-w-[100px]')} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {footer}
      </CardContent>
    </Card>
  );
}

function ResponsesPageSkeleton() {
  return (
    <div className={PAGE_SHELL_WIDE}>
      <PageHeaderSkeleton />
      <Skeleton className="mb-6 h-10 w-72 rounded-lg" />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Skeleton className="h-6 w-32 rounded-full" />
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-10 w-36 rounded-lg" />
          <Skeleton className="h-10 w-32 rounded-lg" />
          <Skeleton className="h-10 w-36 rounded-lg" />
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Skeleton className="h-10 w-24 rounded-lg" />
        <Skeleton className="h-10 w-52 rounded-lg" />
      </div>
      <TableSkeleton
        columns={7}
        rows={20}
        framed={false}
        footer={(
          <div className="flex items-center justify-between gap-3 border-t border-[var(--ba-surface-border)] px-3 py-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-8 w-28 rounded-md" />
          </div>
        )}
      />
    </div>
  );
}

function ResponsesStatsSkeleton() {
  return (
    <div className={PAGE_SHELL}>
      <PageHeaderSkeleton />
      <Skeleton className="mb-6 h-10 w-72 rounded-lg" />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Skeleton className="h-6 w-40 rounded-full" />
        <Skeleton className="h-10 w-36 rounded-lg" />
      </div>
      <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="space-y-3 p-4">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-9 w-16" />
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="mb-8 grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardContent className="p-4">
            <Skeleton className="mb-4 h-5 w-48" />
            <Skeleton className="h-72 w-full rounded-lg" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Skeleton className="mb-4 h-5 w-40" />
            <Skeleton className="h-72 w-full rounded-lg" />
          </CardContent>
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="space-y-3 p-4">
              <Skeleton className="h-5 w-36" />
              <Skeleton className="h-2 w-full rounded-full" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AdminPageSkeleton({ className }) {
  return (
    <div className={cn(PAGE_SHELL, className)}>
      <PageHeaderSkeleton />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Skeleton className="h-6 w-28 rounded-full" />
        <Skeleton className="h-10 w-32 rounded-lg" />
      </div>
      <TableSkeleton columns={6} rows={8} />
    </div>
  );
}

function CardListSkeleton({ count = 4, className }) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex gap-2 pt-1">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PageSkeleton({ className }) {
  return <AdminPageSkeleton className={className} />;
}

function FormSkeleton({ fields = 4, className }) {
  return (
    <div className={cn('space-y-5', className)}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
      <Skeleton className="h-10 w-32" />
    </div>
  );
}

function PlannerSkeleton({ className }) {
  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-6 w-24 rounded-full" />
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-6 w-28 rounded-full" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <Card>
          <CardContent className="space-y-3 p-4">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-9 w-full" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </CardContent>
        </Card>
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="h-80 w-72 shrink-0">
              <CardContent className="space-y-3 p-4">
                <Skeleton className="h-5 w-28" />
                {Array.from({ length: 4 }).map((_, j) => (
                  <Skeleton key={j} className="h-16 w-full rounded-lg" />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export {
  PAGE_SHELL,
  PAGE_SHELL_WIDE,
  Skeleton,
  TableSkeleton,
  CardListSkeleton,
  PageSkeleton,
  AdminPageSkeleton,
  ResponsesPageSkeleton,
  ResponsesStatsSkeleton,
  PageHeaderSkeleton,
  FormSkeleton,
  PlannerSkeleton,
};
